/**
 * Whether a bottom-anchored stream is scrolled away from its newest item, and
 * how many items arrived since. Platform-free: the web hook feeds it DOM
 * metrics, RN screens feed it onScroll metrics. Scroll reports are cheap and
 * set state only when the reader crosses the threshold, so callers may report
 * on every scroll event.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { JUMP_TO_LATEST_THRESHOLD, isNearBottom, nextUnseenCount, type ScrollMetrics } from './jumpToLatest';

export interface JumpToLatestIndicatorOptions {
  itemCount: number;
  /**
   * Identity of the newest item. Only a change here counts as an arrival, so
   * older history loaded in at the top never inflates the count. Defaults to
   * the item count, for append-only lists without stable ids.
   */
  latestKey?: string | number | null;
  /** Changing this (another chat, another conversation) resets to the bottom. */
  scope?: string | null;
  threshold?: number;
}

export function useJumpToLatestIndicator({ itemCount, latestKey = itemCount, scope = null, threshold = JUMP_TO_LATEST_THRESHOLD }: JumpToLatestIndicatorOptions) {
  const nearBottomRef = useRef(true);
  const [nearBottom, setNearBottom] = useState(true);
  const [unseen, setUnseen] = useState(0);
  const seen = useRef({ scope, latestKey, itemCount });

  useEffect(() => {
    const previous = seen.current;
    seen.current = { scope, latestKey, itemCount };
    if (previous.scope !== scope) {
      nearBottomRef.current = true;
      setNearBottom(true);
      setUnseen(0);
      return;
    }
    if (previous.latestKey === latestKey) return;
    setUnseen(count => nextUnseenCount(count, {
      previousCount: previous.itemCount,
      nextCount: itemCount,
      nearBottom: nearBottomRef.current,
    }));
  }, [itemCount, latestKey, scope]);

  const report = useCallback((metrics: ScrollMetrics) => {
    const near = isNearBottom(metrics, threshold);
    if (near === nearBottomRef.current) return near;
    nearBottomRef.current = near;
    setNearBottom(near);
    if (near) setUnseen(0);
    return near;
  }, [threshold]);

  const markAtBottom = useCallback(() => {
    nearBottomRef.current = true;
    setNearBottom(true);
    setUnseen(0);
  }, []);

  return { nearBottom, unseen, nearBottomRef, report, markAtBottom };
}
